import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { RoundedBox, Html, Line } from '@react-three/drei';
import {
  BOAT_DIMENSIONS,
  ROWER_DIMENSIONS,
  RIGGER_DIMENSIONS,
  OAR_DIMENSIONS,
  COLORS,
  MATERIAL_PROPERTIES,
  ANIMATION_CONFIG,
} from './constants';
import { calculateRowingAnimation } from './utils';
import { getSeatTelemetry } from '../../../data/mockCrewMetrics';

export interface RowerAnnotationOptions {
  showSeatLabels: boolean;
  showWatts: boolean;
  showBladeArc: boolean;
  showPerformanceMetrics: boolean;
}

/**
 * Creates a tapered oar blade geometry
 * The blade is full width for 3/4 of its length, then tapers in the last 1/4 (inboard side)
 * @param side - 1 for port (positive Z), -1 for starboard (negative Z)
 */
const createTaperedBladeGeometry = (
  thickness: number,
  height: number,
  width: number,
  side: number
) => {
  const geometry = new THREE.BufferGeometry();
  const positions: number[] = [];
  const indices: number[] = [];

  // Taper ratio: blade tapers to 40% width at base (inboard)
  const taperRatio = 0.4;
  const baseWidth = width * taperRatio;

  // Taper starts at 3/4 of blade height from tip
  const taperStartHeight = height / 4; // Distance from base where taper starts

  // For port (side=1): base at -height/2 (inboard/shaft), tip at +height/2 (outboard)
  // For starboard (side=-1): we need to flip the geometry so taper is at inboard
  const flipFactor = side;

  // Create vertices for the blade (3 cross-sections: base, taper-start, tip)
  // Front face (toward stroke direction)
  positions.push(-thickness / 2, flipFactor * (-height / 2), -baseWidth / 2); // 0: base left front (tapered)
  positions.push(-thickness / 2, flipFactor * (-height / 2), baseWidth / 2); // 1: base right front (tapered)
  positions.push(-thickness / 2, flipFactor * (-height / 2 + taperStartHeight), -width / 2); // 2: taper-start left front (full width)
  positions.push(-thickness / 2, flipFactor * (-height / 2 + taperStartHeight), width / 2); // 3: taper-start right front (full width)
  positions.push(-thickness / 2, flipFactor * (height / 2), -width / 2); // 4: tip left front (full width)
  positions.push(-thickness / 2, flipFactor * (height / 2), width / 2); // 5: tip right front (full width)

  // Back face
  positions.push(thickness / 2, flipFactor * (-height / 2), -baseWidth / 2); // 6: base left back (tapered)
  positions.push(thickness / 2, flipFactor * (-height / 2), baseWidth / 2); // 7: base right back (tapered)
  positions.push(thickness / 2, flipFactor * (-height / 2 + taperStartHeight), -width / 2); // 8: taper-start left back (full width)
  positions.push(thickness / 2, flipFactor * (-height / 2 + taperStartHeight), width / 2); // 9: taper-start right back (full width)
  positions.push(thickness / 2, flipFactor * (height / 2), -width / 2); // 10: tip left back (full width)
  positions.push(thickness / 2, flipFactor * (height / 2), width / 2); // 11: tip right back (full width)

  // Define faces (two triangles per quad)
  // Front face - bottom section (tapered)
  indices.push(0, 2, 1, 1, 2, 3);
  // Front face - top section (full width)
  indices.push(2, 4, 3, 3, 4, 5);

  // Back face - bottom section (tapered)
  indices.push(6, 7, 8, 7, 9, 8);
  // Back face - top section (full width)
  indices.push(8, 9, 10, 9, 11, 10);

  // Left side - bottom (tapered)
  indices.push(0, 6, 2, 2, 6, 8);
  // Left side - top (full width)
  indices.push(2, 8, 4, 4, 8, 10);

  // Right side - bottom (tapered)
  indices.push(1, 3, 7, 3, 9, 7);
  // Right side - top (full width)
  indices.push(3, 5, 9, 5, 11, 9);

  // Top (tip - full width)
  indices.push(4, 10, 5, 5, 10, 11);

  // Bottom (base - tapered width)
  indices.push(0, 1, 6, 1, 7, 6);

  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  return geometry;
};

interface RowerProps {
  x: number;
  seatNumber: number;
  isPort: boolean;
  animationTime: number;
  strokeDuration: number;
  annotationOptions?: RowerAnnotationOptions;
}

/**
 * Rower component - renders an individual rower with rigger, oar, and animation
 * Includes body lean animation synchronized with oar sweep
 */
export const Rower = ({
  x,
  seatNumber,
  isPort,
  animationTime,
  strokeDuration,
  annotationOptions,
}: RowerProps) => {
  const oarGroupRef = useRef<THREE.Group>(null);
  const rowerBodyRef = useRef<THREE.Group>(null);

  const { HULL_HEIGHT_ABOVE_WATER } = BOAT_DIMENSIONS;
  const {
    SEAT_WIDTH,
    SEAT_DEPTH,
    SEAT_HEIGHT,
    SEAT_Y_OFFSET,
    SEAT_TO_RIGGER_OFFSET,
    TORSO_WIDTH,
    TORSO_DEPTH,
    TORSO_HEIGHT,
    HEAD_RADIUS,
    HEAD_SEGMENTS_WIDTH,
    HEAD_SEGMENTS_HEIGHT,
  } = ROWER_DIMENSIONS;
  const {
    RIGGER_LENGTH,
    RIGGER_HEIGHT,
    RIGGER_WIDTH,
    RIGGER_BEAM_HEIGHT,
    GATE_WIDTH,
    GATE_HEIGHT,
    GATE_DEPTH,
  } = RIGGER_DIMENSIONS;
  const {
    OAR_LENGTH,
    OAR_INBOARD,
    SHAFT_DIAMETER,
    SHAFT_SEGMENTS,
    HANDLE_LENGTH,
    HANDLE_SEGMENTS,
    BLADE_THICKNESS,
    BLADE_HEIGHT,
    BLADE_WIDTH,
  } = OAR_DIMENSIONS;

  const seatY = HULL_HEIGHT_ABOVE_WATER + SEAT_Y_OFFSET;
  const gateHeight = HULL_HEIGHT_ABOVE_WATER + RIGGER_HEIGHT;
  const oarOutboard = OAR_LENGTH - OAR_INBOARD;

  // Side multiplier: port = +1 (positive Z), starboard = -1 (negative Z)
  const side = isPort ? 1 : -1;
  const riggerZ = side * RIGGER_LENGTH;

  // Alternate jersey colors
  const jerseyColor = seatNumber % 2 === 0 ? COLORS.JERSEY_EVEN : COLORS.JERSEY_ODD;
  const telemetry = useMemo(() => getSeatTelemetry(seatNumber), [seatNumber]);
  const wattSeries = telemetry.watts;
  const wattSeriesLength = Math.max(1, wattSeries.length);
  const rowerName = telemetry.rowerName;

  // Update watts at end of drive phase (finish position) instead of start of cycle
  const adjustedTime = animationTime - ANIMATION_CONFIG.DRIVE_PHASE_RATIO * strokeDuration;
  const strokeIndex = Math.max(0, Math.floor(adjustedTime / strokeDuration));

  const currentWatt = wattSeries[strokeIndex % wattSeriesLength];
  const previousWatt = wattSeries[(strokeIndex + wattSeriesLength - 1) % wattSeriesLength];
  const wattDelta = currentWatt - previousWatt;
  const trend: 'up' | 'down' | 'flat' = wattDelta > 1 ? 'up' : wattDelta < -1 ? 'down' : 'flat';
  const shouldPulse = trend === 'down';
  const overlayOffsetZ = isPort ? 0.12 : -0.12;
  const showSeatLabels = annotationOptions?.showSeatLabels ?? true;
  const showWatts = annotationOptions?.showWatts ?? true;
  const showBladeArc = annotationOptions?.showBladeArc ?? false;
  const showPerformanceMetrics = annotationOptions?.showPerformanceMetrics ?? false;
  const showOverlay = showSeatLabels || showWatts;
  const overlayClassNames = ['rower-overlay'];
  if (trend === 'up') {
    overlayClassNames.push('rower-overlay--up');
  } else if (trend === 'down') {
    overlayClassNames.push('rower-overlay--down');
  }
  if (shouldPulse) {
    overlayClassNames.push(`rower-overlay--pulse-${strokeIndex}`);
  }
  const trendClassNames = ['rower-overlay__trend', `rower-overlay__trend--${trend}`];

  // Get metrics for arc calculations
  const metrics = telemetry.averages;

  // Calculate arc segments based on slip metrics
  const arcSegments = useMemo(() => {
    if (!showBladeArc) {
      return null;
    }

    // Convert metric distances to angles (radians)
    // angle = arc_length / radius, where radius = oarOutboard
    const metersToRadians = (meters: number) => meters / oarOutboard;

    const catchSlipRad = metersToRadians(metrics.catchSlip);
    const finishSlipRad = metersToRadians(metrics.finishSlip);

    // Define catch and finish angles from animation config
    const catchAngleRad = THREE.MathUtils.degToRad(ANIMATION_CONFIG.OAR_CATCH_ANGLE);
    const finishAngleRad = THREE.MathUtils.degToRad(ANIMATION_CONFIG.OAR_FINISH_ANGLE);

    // Calculate segment boundaries
    const catchSlipStartAngle = catchAngleRad;
    const catchSlipEndAngle = catchAngleRad + catchSlipRad;
    const effectiveLengthStartAngle = catchSlipEndAngle;
    const effectiveLengthEndAngle = finishAngleRad - finishSlipRad;
    const finishSlipStartAngle = effectiveLengthEndAngle;
    const finishSlipEndAngle = finishAngleRad;

    const samples = 20;
    const axis = new THREE.Vector3(0, 1, 0);
    const baseVector = new THREE.Vector3(0, 0, side * oarOutboard);
    const pivot = new THREE.Vector3(0, 0.05, riggerZ); // Slightly above water surface

    const generateArcPoints = (startAngle: number, endAngle: number) => {
      const points: THREE.Vector3[] = [];
      for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const angle = THREE.MathUtils.lerp(startAngle, endAngle, t);
        const rotated = baseVector.clone().applyAxisAngle(axis, angle * side);
        points.push(pivot.clone().add(rotated));
      }
      return points;
    };

    return {
      catchSlip: {
        points: generateArcPoints(catchSlipStartAngle, catchSlipEndAngle),
        color: '#dc2626', // Red
      },
      effectiveLength: {
        points: generateArcPoints(effectiveLengthStartAngle, effectiveLengthEndAngle),
        color: '#16a34a', // Green
      },
      finishSlip: {
        points: generateArcPoints(finishSlipStartAngle, finishSlipEndAngle),
        color: '#eab308', // Yellow
      },
    };
  }, [showBladeArc, side, oarOutboard, riggerZ, metrics]);

  // Create tapered blade geometry (memoized for performance)
  // Side parameter ensures taper is always on the inboard (shaft) side
  const bladeGeometry = useMemo(
    () => createTaperedBladeGeometry(BLADE_THICKNESS, BLADE_HEIGHT, BLADE_WIDTH, side),
    [BLADE_THICKNESS, BLADE_HEIGHT, BLADE_WIDTH, side]
  );

  // Animation: sweep the oar back and forth and lean rower body
  useFrame(() => {
    const { angle, bodyLean } = calculateRowingAnimation(animationTime, strokeDuration);

    if (oarGroupRef.current) {
      // Apply opposite rotation for port vs starboard so blades sweep toward stern together
      oarGroupRef.current.rotation.y = THREE.MathUtils.degToRad(angle * side);
    }

    if (rowerBodyRef.current) {
      // Pitch the rower body forward/back along boat length
      // Tilt in the Y-X plane = rotate around Z-axis
      rowerBodyRef.current.rotation.set(
        0, // no pitch (Y-Z plane)
        0, // no yaw
        THREE.MathUtils.degToRad(bodyLean) // roll in Y-X plane (forward/back along boat)
      );
    }

    // Blade arcs are now static and rendered separately for each segment
  });

  // Seat position is offset toward bow from rigger position
  const seatX = x + SEAT_TO_RIGGER_OFFSET;
  const metricsCardPosition = useMemo(() => {
    const pivotZ = riggerZ;
    const direction = pivotZ >= 0 ? 1 : -1;
    const targetZ = direction * 5;
    const offsetZ = targetZ - pivotZ;
    const height = 0.12;
    return [0, height, offsetZ] as [number, number, number];
  }, [riggerZ]);

  const formatMeters = (value: number) => `${value.toFixed(2)} m`;

  return (
    <group>
      {/* Seat and rower body - positioned toward bow from rigger */}
      <group position={[seatX, seatY, 0]}>
        {/* Seat */}
        <mesh position={[0, 0, 0]} castShadow receiveShadow>
          <boxGeometry args={[SEAT_DEPTH, SEAT_HEIGHT, SEAT_WIDTH]} />
          <meshStandardMaterial
            color={COLORS.SEAT}
            roughness={MATERIAL_PROPERTIES.SEAT_ROUGHNESS}
            metalness={MATERIAL_PROPERTIES.SEAT_METALNESS}
          />
        </mesh>

        {/* Rower body group (torso + head, rotates to lean forward/back) */}
        <group ref={rowerBodyRef} position={[0, SEAT_HEIGHT, 0]}>
          {/* Torso (human-sized) */}
          <RoundedBox
            args={[TORSO_DEPTH, TORSO_HEIGHT, TORSO_WIDTH]}
            radius={Math.min(TORSO_DEPTH, TORSO_WIDTH) * 0.15}
            smoothness={3}
            position={[0, TORSO_HEIGHT / 2, 0]}
            castShadow
            receiveShadow
          >
            <meshStandardMaterial color={jerseyColor} />
          </RoundedBox>

          {/* Head (sphere on top of torso) */}
          <mesh position={[0, TORSO_HEIGHT + HEAD_RADIUS, 0]} castShadow receiveShadow>
            <sphereGeometry args={[HEAD_RADIUS, HEAD_SEGMENTS_WIDTH, HEAD_SEGMENTS_HEIGHT]} />
            <meshStandardMaterial color={COLORS.SKIN_TONE} />
          </mesh>
        </group>
        {showOverlay && (
          <Html
            center
            position={[0, TORSO_HEIGHT + HEAD_RADIUS + 0.65, overlayOffsetZ]}
            distanceFactor={28}
          >
            <div className={overlayClassNames.join(' ')}>
              {showSeatLabels && <span className="rower-overlay__seat">Seat {seatNumber}</span>}
              {rowerName && <span className="rower-overlay__name">{rowerName}</span>}
              {showWatts && (
                <span className="rower-overlay__metric">
                  {currentWatt} W
                  <span className={trendClassNames.join(' ')} aria-hidden="true" />
                </span>
              )}
            </div>
          </Html>
        )}
      </group>

      {/* Rigger and oar - positioned at rigger location (not offset) */}
      <group position={[x, HULL_HEIGHT_ABOVE_WATER, 0]}>
        {/* Rigger (extends from hull to oarlock) */}
        <mesh position={[0, RIGGER_HEIGHT / 2, riggerZ / 2]} castShadow receiveShadow>
          <boxGeometry args={[RIGGER_WIDTH, RIGGER_BEAM_HEIGHT, RIGGER_LENGTH]} />
          <meshStandardMaterial
            color={COLORS.RIGGER}
            roughness={MATERIAL_PROPERTIES.RIGGER_ROUGHNESS}
            metalness={MATERIAL_PROPERTIES.RIGGER_METALNESS}
          />
        </mesh>

        {/* Oarlock/Gate (at end of rigger) */}
        <mesh position={[0, RIGGER_HEIGHT, riggerZ]} castShadow receiveShadow>
          <boxGeometry args={[GATE_WIDTH, GATE_HEIGHT, GATE_DEPTH]} />
          <meshStandardMaterial
            color={COLORS.GATE}
            roughness={MATERIAL_PROPERTIES.GATE_ROUGHNESS}
            metalness={MATERIAL_PROPERTIES.GATE_METALNESS}
          />
        </mesh>

        {/* Oar group (rotates around gate/oarlock pivot point) */}
        <group ref={oarGroupRef} position={[0, gateHeight - HULL_HEIGHT_ABOVE_WATER, riggerZ]}>
          {/* Oar shaft - extends from handle through gate to blade */}
          <mesh
            position={[0, 0, side * (oarOutboard / 2 - OAR_INBOARD / 2)]}
            rotation={[Math.PI / 2, 0, 0]}
            castShadow
          >
            <cylinderGeometry args={[SHAFT_DIAMETER, SHAFT_DIAMETER, OAR_LENGTH, SHAFT_SEGMENTS]} />
            <meshStandardMaterial
              color={COLORS.OAR_SHAFT}
              roughness={MATERIAL_PROPERTIES.SHAFT_ROUGHNESS}
              metalness={MATERIAL_PROPERTIES.SHAFT_METALNESS}
            />
          </mesh>

          {/* Oar handle (inboard end - white wrap for two hands) */}
          <mesh
            position={[0, 0, -side * (OAR_INBOARD + HANDLE_LENGTH / 2)]}
            rotation={[Math.PI / 2, 0, 0]}
            castShadow
          >
            <cylinderGeometry
              args={[SHAFT_DIAMETER, SHAFT_DIAMETER, HANDLE_LENGTH, HANDLE_SEGMENTS]}
            />
            <meshStandardMaterial
              color={COLORS.OAR_HANDLE}
              roughness={MATERIAL_PROPERTIES.SEAT_ROUGHNESS}
              metalness={MATERIAL_PROPERTIES.SEAT_METALNESS}
            />
          </mesh>

          {/* Oar blade (at outboard end) - tapered, rotated 90° around X-axis */}
          <mesh
            position={[0, 0, side * oarOutboard]}
            rotation={[Math.PI / 2, 0, 0]}
            geometry={bladeGeometry}
            castShadow
          >
            <meshStandardMaterial
              color={COLORS.OAR_BLADE}
              roughness={MATERIAL_PROPERTIES.BLADE_ROUGHNESS}
              metalness={MATERIAL_PROPERTIES.BLADE_METALNESS}
            />
          </mesh>
        </group>

        {/* Blade Arc Segments - Catch Slip, Effective Length, Finish Slip */}
        {showBladeArc && arcSegments && (
          <>
            {/* Catch Slip (Red) */}
            <Line
              points={arcSegments.catchSlip.points}
              color={arcSegments.catchSlip.color}
              lineWidth={3}
              transparent
              opacity={0.85}
            />
            {/* Effective Length (Green) */}
            <Line
              points={arcSegments.effectiveLength.points}
              color={arcSegments.effectiveLength.color}
              lineWidth={3}
              transparent
              opacity={0.85}
            />
            {/* Finish Slip (Yellow) */}
            <Line
              points={arcSegments.finishSlip.points}
              color={arcSegments.finishSlip.color}
              lineWidth={3}
              transparent
              opacity={0.85}
            />
          </>
        )}
      </group>

      {showPerformanceMetrics && (
        <group position={[x, gateHeight, riggerZ]}>
          <Html position={metricsCardPosition} distanceFactor={26} sprite>
            <div
              className={`rower-metrics-overlay rower-metrics-overlay--${isPort ? 'port' : 'starboard'}`}
            >
              <div className="rower-metrics-overlay__title">Seat {seatNumber}</div>
              <div className="rower-metrics-overlay__grid">
                <div className="rower-metrics-overlay__item">
                  <span className="rower-metrics-overlay__label">Catch Slip</span>
                  <span className="rower-metrics-overlay__value">
                    {formatMeters(metrics.catchSlip)}
                  </span>
                </div>
                <div className="rower-metrics-overlay__item">
                  <span className="rower-metrics-overlay__label">Effective Len.</span>
                  <span className="rower-metrics-overlay__value">
                    {formatMeters(metrics.effectiveLength)}
                  </span>
                </div>
                <div className="rower-metrics-overlay__item">
                  <span className="rower-metrics-overlay__label">Finish Slip</span>
                  <span className="rower-metrics-overlay__value">
                    {formatMeters(metrics.finishSlip)}
                  </span>
                </div>
                <div className="rower-metrics-overlay__item">
                  <span className="rower-metrics-overlay__label">Total Len.</span>
                  <span className="rower-metrics-overlay__value">
                    {formatMeters(metrics.totalLength)}
                  </span>
                </div>
              </div>
            </div>
          </Html>
        </group>
      )}
    </group>
  );
};
